import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  TrendingUp, TrendingDown, Minus,
  ShieldCheck, GaugeCircle, Activity, Layers3,
  Zap, ChevronRight, ChevronLeft, ChevronUp, X, FileText,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../data/authStore";

/* ═══════════════════════════════════════════════════════════════════
   Deal Room Intelligence Rail (collapsible)
   ───────────────────────────────────────────────────────────────────
   Two working styles supported:
     1. Operators who want constant intelligence visibility (expanded)
     2. Operators who prefer timeline-first focus (collapsed → slim tab)

   Preference is persisted PER USER + PER DEAL in localStorage so
   collapsing on Deal A never affects Deal B.

   Auto-reopen behaviour:
     The rail re-opens itself (subtly, with an attention pulse) when a
     deal becomes operationally critical — Slipping exchange, Slipping
     completion, chain health ≤ 35, or any critical-urgency intervention.
     The operator can dismiss the attention state for the session.

   Visual identity (unchanged):
     • Same Deal Room cardStyle tokens (rounded-2xl, border-slate-200,
       shadow-sm, bg-white)
     • Same calm 5-band palette as ForecastStrip
     • Collapse animation is a width + opacity fade only — no slide,
       no bounce. Easing cubic-bezier(0.4, 0, 0.2, 1) at 280ms.
   ─────────────────────────────────────────────────────────────────── */

const STORAGE_PREFIX = "cf:intel-rail:collapsed";
const ATTENTION_DISMISS_PREFIX = "cf:intel-rail:attn-dismissed";

const TONE = {
  Strong:   { bg: "rgba(16,185,129,0.08)",  fg: "#047857", dot: "#10B981" },
  Stable:   { bg: "rgba(16,185,129,0.06)",  fg: "#0F766E", dot: "#0EA5A8" },
  Watching: { bg: "rgba(99,102,241,0.07)",  fg: "#4338CA", dot: "#6366F1" },
  "At Risk":{ bg: "rgba(245,158,11,0.10)",  fg: "#92400E", dot: "#F59E0B" },
  Slipping: { bg: "rgba(244,63,94,0.08)",   fg: "#9F1239", dot: "#F43F5E" },
  default:  { bg: "rgba(148,163,184,0.10)", fg: "#475569", dot: "#94A3B8" },
};

const URGENCY_TONE = {
  critical:      { bg: "rgba(244,63,94,0.10)",   fg: "#9F1239" },
  important:     { bg: "rgba(245,158,11,0.12)",  fg: "#92400E" },
  informational: { bg: "rgba(148,163,184,0.14)", fg: "#475569" },
};

const MOMENTUM_TONE = {
  accelerating: { fg: "#047857", label: "Accelerating", icon: TrendingUp },
  stable:       { fg: "#475569", label: "Stable",       icon: Minus },
  slowing:      { fg: "#92400E", label: "Slowing",      icon: TrendingDown },
  silent:       { fg: "#9F1239", label: "Silent",       icon: Activity },
};

// Calm enterprise easing — same curve as ForecastStrip transitions
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/* ─── Helpers ────────────────────────────────────────────────────── */
const bandFromHealth = (h) => {
  if (h == null) return null;
  if (h >= 80) return "Strong";
  if (h >= 65) return "Stable";
  if (h >= 50) return "Watching";
  if (h >= 35) return "At Risk";
  return "Slipping";
};

const detectCriticalState = (intel) => {
  if (!intel) return null;
  const reasons = [];
  if (intel.exchange?.band === "Slipping") reasons.push("Exchange confidence is Slipping");
  if (intel.completion?.band === "Slipping") reasons.push("Completion confidence is Slipping");
  if (intel.chain?.chain_health != null && intel.chain.chain_health <= 35) {
    reasons.push(`Chain health ${intel.chain.chain_health}/100`);
  }
  const criticalIntervention = (intel.interventions || []).find((i) => i.urgency === "critical");
  if (criticalIntervention) reasons.push(criticalIntervention.recommendation);
  // Mortgage expiry signal
  const contribs = [
    ...(intel.exchange?.contributions || []),
    ...(intel.completion?.contributions || []),
  ];
  const mortgageDanger = contribs.find((c) =>
    (c.evidence || "").toLowerCase().includes("mortgage") && (c.delta || 0) <= -12
  );
  if (mortgageDanger) reasons.push(mortgageDanger.evidence);
  // Document blockers that are overdue
  const overdueBlocker = (intel.dependencies?.blockers || []).find((b) => b.is_overdue);
  if (overdueBlocker) reasons.push(`${overdueBlocker.label} overdue`);
  return reasons.length ? reasons[0] : null;
};

const Hairline = () => <div className="h-px bg-slate-100" />;

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="py-3" data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={11} className="text-slate-400 shrink-0" />
      <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-500">
        {title}
      </p>
    </div>
    {children}
  </div>
);

const Band = ({ band, score, label, testid }) => {
  const t = TONE[band] || TONE.default;
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testid}>
      <div className="min-w-0 flex items-center gap-2">
        <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: t.dot }} />
        <span className="text-[12.5px] font-semibold text-slate-900 truncate">{label}</span>
      </div>
      <div className="flex items-baseline gap-1 shrink-0">
        <span className="text-[15px] font-bold tabular-nums" style={{ color: t.fg }}>
          {score ?? "—"}
        </span>
        <span className="text-[9.5px] uppercase tracking-wider font-semibold"
              style={{ color: t.fg }}>
          {band || "—"}
        </span>
      </div>
    </div>
  );
};

const Metric = ({ label, value, fg }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
      {label}
    </span>
    <span className="text-[12px] font-semibold tabular-nums"
          style={{ color: fg || "#0F172A" }}>
      {value}
    </span>
  </div>
);

/* ─── Loading state ───────────────────────────────────────────────── */
const RailSkeleton = () => (
  <section
    className="rounded-2xl bg-white border border-slate-200 shadow-sm shrink-0"
    data-testid="dealroom-intel-rail-loading"
  >
    <div className="px-4 py-3">
      <div className="h-3 w-32 bg-slate-100 rounded  cf-pulse mb-2" />
      <div className="h-5 w-20 bg-slate-100 rounded cf-pulse" />
    </div>
    <Hairline />
    <div className="px-4 py-3 space-y-3">
      {[0,1,2].map(i => (
        <div key={i} className="h-4 bg-slate-100 rounded cf-pulse" />
      ))}
    </div>
  </section>
);

/* ─── Rail body — renders the section list. Used by both desktop and
       mobile expanded states so the content is identical. ─────────── */
const RailBody = ({ intel, chainBand, topInterventions }) => {
  const exchange   = intel.exchange;
  const completion = intel.completion;
  const chain      = intel.chain;
  const velocity   = intel.velocity || {};
  return (
    <div className="px-4">
      {(exchange || completion) ? (
        <>
          <Section icon={GaugeCircle} title="Confidence" testid="intel-rail-confidence">
            <div className="space-y-2">
              {exchange ? (
                <Band band={exchange.band} score={exchange.score} label="Exchange"
                      testid="intel-rail-exchange-band" />
              ) : null}
              {completion ? (
                <Band band={completion.band} score={completion.score} label="Completion"
                      testid="intel-rail-completion-band" />
              ) : null}
            </div>
            {(() => {
              const contribs = [
                ...((exchange?.contributions) || []),
                ...((completion?.contributions) || []),
              ];
              const weakest = contribs
                .filter((c) => (c.delta || 0) < 0)
                .sort((a, b) => (a.delta || 0) - (b.delta || 0))
                .slice(0, 2);
              if (!weakest.length) return null;
              return (
                <ul className="mt-2 space-y-0.5">
                  {weakest.map((c, idx) => (
                    <li key={idx} className="text-[11px] text-slate-500 leading-relaxed flex items-start gap-1.5">
                      <span className="inline-block w-1 h-1 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                      <span className="min-w-0">{c.evidence}</span>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </Section>
          <Hairline />
        </>
      ) : null}

      {chainBand ? (
        <>
          <Section icon={Layers3} title="Chain" testid="intel-rail-chain">
            <Band band={chainBand} score={chain.chain_health} label="Chain health" />
            {chain.position ? (
              <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                Position: <span className="font-semibold text-slate-700">{chain.position}</span>
                {chain.weakest_link?.name && chain.weakest_link.name !== chain.name ? (
                  <> · Weakest link: {chain.weakest_link.name}</>
                ) : null}
              </p>
            ) : null}
            {chain.upstream_drift?.headline ? (
              <div
                className="mt-2 pt-2 border-t border-slate-100"
                data-testid="intel-rail-chain-upstream-drift"
              >
                <div className="flex items-start gap-1.5">
                  <span
                    className="inline-block w-1 h-1 rounded-full mt-1.5 shrink-0"
                    style={{ background: "#94A3B8" }}
                    aria-hidden
                  />
                  <p className="text-[11px] text-slate-500 leading-snug min-w-0">
                    <span className="font-semibold text-slate-700">
                      Linked chain pressure
                    </span>
                    {" · "}
                    {chain.upstream_drift.headline}
                    {chain.upstream_drift.score_delta_completion ? (
                      <span className="tabular-nums">
                        {" "}
                        ({chain.upstream_drift.score_delta_completion} pts to completion)
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
            ) : null}
          </Section>
          <Hairline />
        </>
      ) : null}

      {(velocity?.vs_office || velocity?.office_baseline) ? (
        <>
          <Section icon={Activity} title="Velocity" testid="intel-rail-velocity">
            {velocity.vs_office ? (
              <>
                <Metric label="Offer→Exch" value={`${velocity.vs_office.deal}d`} />
                <Metric label={velocity.office_label || "Office median"}
                        value={`${velocity.vs_office.office}d`} />
                <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                  <p className="text-[11px]" style={{
                    color: velocity.vs_office.tone === "ahead" ? "#047857"
                         : velocity.vs_office.tone === "behind" ? "#92400E"
                         : "#475569",
                  }}>
                    {velocity.vs_office.tone === "ahead"
                      ? `Ahead of office median by ${Math.abs(velocity.vs_office.delta)}d`
                      : velocity.vs_office.tone === "behind"
                      ? `Behind office median by ${velocity.vs_office.delta}d`
                      : "On pace with office median"}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Velocity context not yet available — needs offer→exchange data.
              </p>
            )}
          </Section>
          <Hairline />
        </>
      ) : null}

      {topInterventions.length ? (
        <Section icon={Zap} title="Recommended interventions" testid="intel-rail-interventions">
          <div className="space-y-2">
            {topInterventions.map((it) => {
              const t = URGENCY_TONE[it.urgency] || URGENCY_TONE.informational;
              return (
                <div key={it.id}
                     className="p-2 rounded-lg"
                     style={{ border: "1px solid #E2E8F0" }}
                     data-testid={`intel-rail-intervention-${it.category}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-[11.5px] font-semibold leading-snug text-slate-900 min-w-0">
                      {it.recommendation}
                    </p>
                    <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: t.bg, color: t.fg }}>
                      {it.urgency}
                    </span>
                  </div>
                  {it.evidence?.length ? (
                    <p className="text-[10.5px] text-slate-500 leading-snug truncate">
                      {it.evidence[0]}
                    </p>
                  ) : null}
                  {it.impact?.fee_at_risk_gbp ? (
                    <p className="text-[10px] mt-1 font-semibold tabular-nums" style={{ color: "#9F1239" }}>
                      Fee at risk · £{it.impact.fee_at_risk_gbp.toLocaleString()}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Section>
      ) : null}

      {/* Dependencies — document blockers */}
      {intel.dependencies?.blockers?.length ? (
        <>
          <Hairline />
          <Section icon={FileText} title="Dependencies" testid="intel-rail-deps">
            <p className="text-[11px] text-slate-500 leading-snug mb-2">
              {intel.dependencies.summary.blocking_exchange ? (
                <>{intel.dependencies.summary.blocking_exchange} blocking exchange</>
              ) : null}
              {intel.dependencies.summary.blocking_exchange && intel.dependencies.summary.blocking_completion ? " · " : null}
              {intel.dependencies.summary.blocking_completion ? (
                <>{intel.dependencies.summary.blocking_completion} blocking completion</>
              ) : null}
              {intel.dependencies.summary.overdue ? (
                <span className="text-rose-700"> · {intel.dependencies.summary.overdue} overdue</span>
              ) : null}
            </p>
            <div className="space-y-1.5">
              {intel.dependencies.blockers.slice(0, 4).map((b) => {
                const overdueTone = b.is_overdue
                  ? { bg: "rgba(244,63,94,0.10)", fg: "#9F1239", label: "Overdue" }
                  : b.blocks_completion
                  ? { bg: "rgba(99,102,241,0.10)", fg: "#4338CA", label: "Completion" }
                  : { bg: "rgba(245,158,11,0.12)", fg: "#92400E", label: "Exchange" };
                return (
                  <div
                    key={b.key}
                    className="p-2 rounded-lg"
                    style={{ border: "1px solid #E2E8F0" }}
                    data-testid={`intel-rail-dep-${b.key}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[11.5px] font-semibold text-slate-900 leading-snug truncate">
                          {b.label}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          {b.responsible_label}
                          {b.days_outstanding > 0 ? ` · ${b.days_outstanding}d outstanding` : null}
                        </p>
                      </div>
                      <span
                        className="shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: overdueTone.bg, color: overdueTone.fg }}
                      >
                        {overdueTone.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        </>
      ) : null}

      {!intel.exchange && !intel.completion && !chainBand && !topInterventions.length && !intel.dependencies?.blockers?.length ? (
        <Section icon={ShieldCheck} title="Status" testid="intel-rail-empty">
          <p className="text-[11.5px] text-slate-500 leading-relaxed">
            No operational signals flagged — this deal is progressing within expected parameters.
          </p>
        </Section>
      ) : null}

      <div className="pb-2" />
    </div>
  );
};

/* ─── Desktop: slim collapsed tab ─────────────────────────────────── */
const CollapsedTab = ({
  intel, chainBand, onExpand, momentumMeta, criticalState, testidSuffix = "",
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // The collapsed tab shows one health dot (chain band) + one confidence
  // pulse dot (worst of exchange/completion). Everything else lives in
  // the tooltip on hover.
  const healthBand = chainBand
    || intel.exchange?.band
    || intel.completion?.band
    || "Stable";
  const worstConfidence =
    intel.exchange?.band === "Slipping" || intel.completion?.band === "Slipping"
      ? "Slipping"
      : intel.exchange?.band === "At Risk" || intel.completion?.band === "At Risk"
      ? "At Risk"
      : intel.exchange?.band || intel.completion?.band || "Stable";
  const healthTone = TONE[healthBand] || TONE.default;
  const confidenceTone = TONE[worstConfidence] || TONE.default;
  const MomentumIcon = momentumMeta?.icon || Minus;

  return (
    <div
      className="relative rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col items-center py-3 px-2 transition-all shrink-0"
      style={{
        width: 44,
        transition: `all 280ms ${EASE}`,
      }}
      data-testid={`dealroom-intel-rail-collapsed${testidSuffix}`}
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
      onFocus={() => setTooltipOpen(true)}
      onBlur={() => setTooltipOpen(false)}
    >
      <button
        type="button"
        onClick={onExpand}
        className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/60 transition-colors"
        style={{ transition: `all 200ms ${EASE}` }}
        aria-label="Expand intelligence rail"
        data-testid={`intel-rail-restore${testidSuffix}`}
        title="Expand intelligence rail"
      >
        <ChevronLeft size={14} />
      </button>

      <div className="h-px w-6 bg-slate-100 my-2" />

      {/* Health dot + (only on critical) gentle pulse */}
      <div className="relative flex flex-col items-center gap-2 py-2">
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{
            background: healthTone.dot,
            boxShadow: criticalState ? `0 0 0 4px ${healthTone.dot}1A` : "none",
            animation: criticalState ? "cfIntelPulse 2.4s ease-in-out infinite" : "none",
          }}
          aria-label={`Deal health · ${healthBand}`}
          data-testid={`intel-rail-collapsed-health-dot${testidSuffix}`}
        />
        <span
          className="inline-block w-2 h-2 rounded-full"
          style={{
            background: confidenceTone.dot,
            boxShadow: criticalState ? `0 0 0 4px ${confidenceTone.dot}1A` : "none",
            animation: criticalState ? "cfIntelPulse 2.4s ease-in-out infinite" : "none",
          }}
          aria-label={`Confidence · ${worstConfidence}`}
          data-testid={`intel-rail-collapsed-confidence-dot${testidSuffix}`}
        />
        {momentumMeta ? (
          <MomentumIcon
            size={11}
            style={{ color: momentumMeta.fg }}
            aria-label={`Momentum · ${momentumMeta.label}`}
          />
        ) : null}
      </div>

      {/* Tooltip */}
      {tooltipOpen ? (
        <div
          className="absolute right-full mr-2 top-2 rounded-lg bg-slate-900 text-white px-3 py-2 shadow-lg pointer-events-none"
          style={{
            width: 200,
            opacity: 0.96,
            transition: `opacity 200ms ${EASE}`,
          }}
          role="tooltip"
        >
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-1">
            Intelligence
          </p>
          <p className="text-[11.5px] leading-snug text-slate-100">
            <span style={{ color: healthTone.dot }}>●</span> Health · {healthBand}
            <br />
            <span style={{ color: confidenceTone.dot }}>●</span> Confidence · {worstConfidence}
            {momentumMeta ? <><br />Momentum · {momentumMeta.label}</> : null}
          </p>
          {criticalState ? (
            <p className="text-[10.5px] mt-1.5 pt-1.5 border-t border-slate-700/50 leading-snug text-rose-300">
              {criticalState}
            </p>
          ) : null}
          <p className="text-[10px] mt-1.5 text-slate-400">Click to expand</p>
        </div>
      ) : null}
    </div>
  );
};

/* ─── Public component ───────────────────────────────────────────── */
export const DealIntelligenceRail = ({ dealId }) => {
  const { user } = useAuth();
  const userId = user?.id || "anon";

  const storageKey = useMemo(
    () => `${STORAGE_PREFIX}:${userId}:${dealId || ""}`,
    [userId, dealId],
  );
  const dismissKey = useMemo(
    () => `${ATTENTION_DISMISS_PREFIX}:${userId}:${dealId || ""}`,
    [userId, dealId],
  );

  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // userCollapsed is the operator's stored preference. attentionForcedOpen
  // is computed: when a critical state appears AND the user has not
  // dismissed the attention banner for the session, we present the
  // expanded rail with a subtle attention pulse.
  const [userCollapsed, setUserCollapsed] = useState(() => {
    try { return window.localStorage.getItem(storageKey) === "1"; }
    catch { return false; }
  });
  const [attentionDismissed, setAttentionDismissed] = useState(() => {
    try { return window.sessionStorage.getItem(dismissKey) === "1"; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Re-read prefs when the deal changes
  useEffect(() => {
    try { setUserCollapsed(window.localStorage.getItem(storageKey) === "1"); }
    catch { setUserCollapsed(false); }
    try { setAttentionDismissed(window.sessionStorage.getItem(dismissKey) === "1"); }
    catch { setAttentionDismissed(false); }
  }, [storageKey, dismissKey]);

  // Fetch intel
  useEffect(() => {
    if (!dealId) return;
    let alive = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        const { data } = await api.get(`/api/deals/${dealId}/intel-rail`);
        if (alive) setIntel(data?.intel || null);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dealId]);

  const criticalState = useMemo(() => detectCriticalState(intel), [intel]);

  const persistCollapsed = useCallback((next) => {
    setUserCollapsed(next);
    try {
      if (next) window.localStorage.setItem(storageKey, "1");
      else window.localStorage.removeItem(storageKey);
    } catch { /* noop */ }
  }, [storageKey]);

  const dismissAttention = useCallback(() => {
    setAttentionDismissed(true);
    try { window.sessionStorage.setItem(dismissKey, "1"); } catch { /* noop */ }
  }, [dismissKey]);

  // Collapsed when: user chose to collapse AND there's no un-dismissed critical state.
  const isCollapsed = userCollapsed && !(criticalState && !attentionDismissed);
  const isAttentionForced = userCollapsed && criticalState && !attentionDismissed;

  if (loading) return <RailSkeleton />;
  if (error || !intel) return null;

  const interventions = intel.interventions || [];
  const chainBand = bandFromHealth(intel.chain?.chain_health);
  const momentumMeta = intel.momentum
    ? (MOMENTUM_TONE[intel.momentum.direction] || MOMENTUM_TONE.stable)
    : null;
  const MomentumIcon = momentumMeta?.icon || Minus;
  const topInterventions = interventions.slice(0, 2);

  /* ─── DESKTOP — collapsed slim tab ─────────────────────────────── */
  if (isCollapsed) {
    return (
      <>
        <style>{KEYFRAMES}</style>
        <CollapsedTab
          intel={intel}
          chainBand={chainBand}
          onExpand={() => persistCollapsed(false)}
          momentumMeta={momentumMeta}
          criticalState={criticalState}
        />
        <MobileTrigger
          intel={intel}
          chainBand={chainBand}
          momentumMeta={momentumMeta}
          onOpen={() => setMobileOpen(true)}
          criticalState={criticalState}
        />
        {mobileOpen ? (
          <MobileDrawer
            intel={intel}
            chainBand={chainBand}
            topInterventions={topInterventions}
            onClose={() => setMobileOpen(false)}
          />
        ) : null}
      </>
    );
  }

  /* ─── DESKTOP — expanded full rail ─────────────────────────────── */
  return (
    <>
      <style>{KEYFRAMES}</style>

      <section
        className="rounded-2xl bg-white border border-slate-200 shadow-sm hidden xl:block shrink-0"
        style={{
          transition: `all 280ms ${EASE}`,
          boxShadow: isAttentionForced
            ? "0 0 0 1px rgba(244,63,94,0.18), 0 6px 18px -8px rgba(244,63,94,0.18)"
            : undefined,
        }}
        data-testid="dealroom-intel-rail"
      >
        {/* Attention banner — calm, dismissible, top of rail */}
        {isAttentionForced ? (
          <div
            className="flex items-start gap-2 px-4 py-2"
            style={{ background: "rgba(244,63,94,0.06)" }}
            data-testid="intel-rail-attention-banner"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
              style={{
                background: "#F43F5E",
                animation: "cfIntelPulse 2.4s ease-in-out infinite",
              }}
            />
            <p className="text-[11px] leading-snug text-rose-900 flex-1 min-w-0">
              Reopened — {criticalState}
            </p>
            <button
              type="button"
              onClick={dismissAttention}
              className="shrink-0 -mt-0.5 -mr-1 w-5 h-5 inline-flex items-center justify-center rounded text-rose-700 hover:bg-rose-100/60"
              style={{ transition: `all 200ms ${EASE}` }}
              aria-label="Dismiss attention notice"
              data-testid="intel-rail-attention-dismiss"
            >
              <X size={10} />
            </button>
          </div>
        ) : null}

        {/* Header */}
        <div
          className="px-4 pt-3.5 pb-3 flex items-center justify-between transition-opacity"
          style={{ transition: `opacity 280ms ${EASE}` }}
        >
          <div>
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-indigo-700">
              Intelligence Rail
            </p>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Live operational signals for this deal
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {momentumMeta ? (
              <div
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
                style={{ background: "rgba(99,102,241,0.06)" }}
                data-testid="intel-rail-momentum-pill"
              >
                <MomentumIcon size={11} style={{ color: momentumMeta.fg }} />
                <span className="text-[10.5px] font-semibold" style={{ color: momentumMeta.fg }}>
                  {momentumMeta.label}
                </span>
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => persistCollapsed(true)}
              className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-indigo-50/60 transition-colors"
              style={{ transition: `all 200ms ${EASE}` }}
              aria-label="Collapse intelligence rail"
              data-testid="intel-rail-collapse"
              title="Collapse intelligence rail"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>

        <Hairline />
        <RailBody intel={intel} chainBand={chainBand} topInterventions={topInterventions} />
      </section>

      {/* MOBILE — bottom intelligence trigger pill */}
      <MobileTrigger
        intel={intel}
        chainBand={chainBand}
        momentumMeta={momentumMeta}
        onOpen={() => setMobileOpen(true)}
        criticalState={criticalState}
      />
      {mobileOpen ? (
        <MobileDrawer
          intel={intel}
          chainBand={chainBand}
          topInterventions={topInterventions}
          onClose={() => setMobileOpen(false)}
        />
      ) : null}
    </>
  );
};

/* ─── Mobile bottom trigger pill ──────────────────────────────────
   Default partially-collapsed: a single calm horizontal pill anchored
   above the existing mobile bottom-nav. Tap → opens the drawer.
   Hidden on xl breakpoint where the inline rail is visible. ─────── */
const MobileTrigger = ({ intel, chainBand, momentumMeta, onOpen, criticalState }) => {
  const healthBand = chainBand
    || intel.exchange?.band || intel.completion?.band || "Stable";
  const t = TONE[healthBand] || TONE.default;
  const MomentumIcon = momentumMeta?.icon || Minus;
  return createPortal(
    <button
      type="button"
      onClick={onOpen}
      className="xl:hidden fixed left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur-md border border-slate-200 shadow-lg px-3 py-1.5"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)",
        zIndex: 50,
        transition: `all 240ms ${EASE}`,
        boxShadow: criticalState
          ? "0 0 0 1px rgba(244,63,94,0.18), 0 10px 26px -10px rgba(15,23,42,0.18)"
          : "0 10px 26px -10px rgba(15,23,42,0.18)",
      }}
      data-testid="intel-rail-mobile-trigger"
      aria-label="Open intelligence drawer"
    >
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{
          background: t.dot,
          animation: criticalState ? "cfIntelPulse 2.4s ease-in-out infinite" : "none",
        }}
      />
      <span className="text-[11.5px] font-semibold text-slate-900">Intelligence</span>
      <span className="text-[10.5px] text-slate-500 uppercase tracking-wider">{healthBand}</span>
      {momentumMeta ? (
        <MomentumIcon size={11} style={{ color: momentumMeta.fg }} />
      ) : null}
      <ChevronUp size={11} className="text-slate-400" />
    </button>,
    document.body,
  );
};

/* ─── Mobile bottom drawer ───────────────────────────────────────── */
const MobileDrawer = ({ intel, chainBand, topInterventions, onClose }) => {
  // Lock scroll behind the drawer
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  const sheetRef = useRef(null);
  return createPortal(
    <div
      className="xl:hidden fixed inset-0 z-50 flex flex-col justify-end"
      style={{ background: "rgba(15,23,42,0.32)", animation: "cfIntelFadeIn 200ms ease-out forwards" }}
      onClick={onClose}
      data-testid="intel-rail-mobile-drawer"
    >
      <div
        ref={sheetRef}
        className="bg-white rounded-t-3xl shadow-2xl overflow-hidden"
        style={{
          maxHeight: "82vh",
          animation: "cfIntelDrawerUp 280ms cubic-bezier(0.4, 0, 0.2, 1) forwards",
          paddingBottom: "max(env(safe-area-inset-bottom, 16px), 16px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="pt-2 pb-1 flex justify-center">
          <div className="w-10 h-1 rounded-full bg-slate-200" />
        </div>
        <div className="px-4 pt-1 pb-2 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-indigo-700">
            Intelligence
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700"
            aria-label="Close intelligence drawer"
            data-testid="intel-rail-mobile-close"
          >
            <X size={14} />
          </button>
        </div>
        <Hairline />
        <div className="overflow-y-auto" style={{ maxHeight: "calc(82vh - 60px)" }}>
          <RailBody intel={intel} chainBand={chainBand} topInterventions={topInterventions} />
        </div>
      </div>
    </div>,
    document.body,
  );
};

/* ─── CSS keyframes — calm, low-amplitude ────────────────────────── */
const KEYFRAMES = `
@keyframes cfIntelPulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.55; transform: scale(1.18); }
}
@keyframes cfIntelFadeIn {
  from { opacity: 0; } to { opacity: 1; }
}
@keyframes cfIntelDrawerUp {
  from { transform: translateY(24px); opacity: 0.6; }
  to   { transform: translateY(0);    opacity: 1; }
}
`;

export default DealIntelligenceRail;
